import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "./WelcomePage.module.css";
import LandingHeader from "./components/LandingHeader";
import LandingHero from "./components/LandingHero";
import HomeBannerCarousel from "../../../components/banners/HomeBannerCarousel";
import useBanners from "../../../hooks/useBanners";
import LandingFeatures from "./components/LandingFeatures";
import LandingHowToPlay from "./components/LandingHowToPlay";
import LandingConversion from "./components/LandingConversion";
import LandingLegalLotteries from "./components/LandingLegalLotteries";
import LandingFooter from "./components/LandingFooter";
import LandingBlogTeaser from "./components/LandingBlogTeaser";
import LandingPromoTeaser from "./components/LandingPromoTeaser";
import LandingPromoterCard from "./components/LandingPromoterCard";
import WelcomeBonusStrip from "../../../components/bonus/WelcomeBonusStrip";
import { marketingService } from "../../../services/api";
import {
  saveReferralCode,
  saveUtmFromSearch,
  saveLandingSlug,
} from "../../../services/campaignAttribution";
import { mergeLandingContent } from "../../../services/landingContentNormalize";
import { LANDING_FALLBACK } from "./landingContent";
import useReveal from "../../../hooks/useReveal";

const REVEAL_OPTS = { threshold: 0, rootMargin: "0px 0px -8% 0px" };

const WelcomePage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const heroRef = useRef(null);
  const [heroReady, setHeroReady] = useState(false);
  const [content, setContent] = useState(() =>
    mergeLandingContent(LANDING_FALLBACK, null, slug)
  );
  const [isLoadingLanding, setIsLoadingLanding] = useState(Boolean(slug));

  useEffect(() => {
    saveUtmFromSearch(searchParams);
    if (slug) {
      saveReferralCode(slug);
      saveLandingSlug(slug);
    }
  }, [slug, searchParams]);

  useEffect(() => {
    setHeroReady(true);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setIsLoadingLanding(Boolean(slug));

      const [defaultResult, slugResult] = await Promise.all([
        marketingService.getDefaultLanding(),
        slug
          ? marketingService.getLandingBySlug(slug)
          : Promise.resolve({ ok: false, data: null }),
      ]);

      if (!isMounted) return;

      const defaultData =
        defaultResult.ok && defaultResult.data
          ? defaultResult.data
          : LANDING_FALLBACK;

      let campaignData = null;
      if (slug && slugResult.ok && slugResult.data) {
        campaignData = slugResult.data;
        const overlay = campaignData.campaign_overlay || campaignData;
        const code =
          overlay.referral_code ||
          overlay.campaign_code ||
          campaignData.referral_code;
        if (code) saveReferralCode(String(code));
      } else if (slug) {
        campaignData = { slug, page_type: "campaign" };
      }

      setContent(mergeLandingContent(defaultData, campaignData, slug));
      setIsLoadingLanding(false);
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  const hasLandingSlug = Boolean(slug);
  const overlay = content.campaign_overlay;

  const promoterLabel =
    overlay?.promoter_name ||
    overlay?.campaign_code ||
    slug ||
    "Gold";

  const ctaLinkStr =
    typeof overlay?.cta_link === "string" ? overlay.cta_link.trim() : "";
  const registerHref = ctaLinkStr.startsWith("http")
    ? ctaLinkStr
    : overlay?.cta_link || "/auth";

  const rFeatures = useReveal(REVEAL_OPTS);
  const rHow = useReveal(REVEAL_OPTS);
  const rConversion = useReveal(REVEAL_OPTS);
  const rLegalLotteries = useReveal(REVEAL_OPTS);
  const rBlog = useReveal(REVEAL_OPTS);
  const rPromos = useReveal(REVEAL_OPTS);

  const sectionReveal = (reveal) =>
    `${styles.revealSection} ${reveal.isVisible ? styles.revealSectionVisible : ""}`;

  const hero = {
    ...content.hero,
    title: overlay?.custom_title || content.hero.title,
    subtitle: overlay?.custom_subtitle || content.hero.subtitle,
    promo_image_url: overlay?.hero_image_url || content.hero.promo_image_url,
    cta_text: overlay?.cta_text || content.hero.cta_text,
    cta_link: overlay?.cta_link || content.hero.cta_link,
  };

  const { banners: homeBanners } = useBanners("HOME_TOP");

  return (
    <div className={styles.page}>
      <LandingHeader />

      {/* Banners del admin (Marketing → Banners, posición "Home"): si hay, reemplazan el
          banner fijo; si no, se ve el de siempre. */}
      {homeBanners.length > 0 ? (
        <HomeBannerCarousel banners={homeBanners} />
      ) : (
        <LandingHero
          ref={heroRef}
          hero={hero}
          heroReady={heroReady}
        />
      )}

      {/* Bono de bienvenida vigente (se oculta si el programa está inactivo) */}
      <div className={styles.welcomeBonusWrap}>
        <WelcomeBonusStrip />
      </div>

      {hasLandingSlug && (
        <LandingPromoterCard
          promoterLabel={promoterLabel}
          isLoading={isLoadingLanding}
          registerHref={registerHref}
          ctaText={overlay?.cta_text || "Registrarme Ahora"}
        />
      )}

      <LandingFeatures
        section={content.features_section}
        reveal={rFeatures}
        sectionReveal={sectionReveal}
      />

      <LandingPromoTeaser reveal={rPromos} sectionReveal={sectionReveal} />

      <LandingHowToPlay
        section={content.how_to_play_section}
        reveal={rHow}
        sectionReveal={sectionReveal}
      />

      <LandingConversion
        section={content.conversion_section}
        reveal={rConversion}
        sectionReveal={sectionReveal}
      />

      <LandingLegalLotteries
        section={content.legal_lotteries_section}
        reveal={rLegalLotteries}
        sectionReveal={sectionReveal}
      />

      <LandingBlogTeaser reveal={rBlog} sectionReveal={sectionReveal} />

      <LandingFooter footer={content.footer_section} />
    </div>
  );
};

export default WelcomePage;
